// ============================================================================
// Doc Order — first-payment / doc-dating / staleness rules (spec A1.10–A1.12).
// Every "today" comparison takes an injected `now` (the edge function passes
// the Pacific business date; the client passes render-time new Date()).
// ============================================================================

import { DO_CONFIG } from './config.ts';

/** 'YYYY-MM-DD' (or 'YYYY-MM') → absolute month index (year*12 + month-1). */
export function monthIndex(dateStr: string | null | undefined): number | null {
  if (!dateStr || dateStr.length < 7) return null;
  const [y, m] = dateStr.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  return y * 12 + (m - 1);
}

/** Month index → 'YYYY-MM' (month-input value). */
export function monthValue(idx: number): string {
  const y = Math.floor(idx / 12);
  const m = idx % 12;
  return `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** First payment = doc month + offset (default 2 — May docs → July first payment). */
export function firstPayment(baseMonthIdx: number | null): string {
  return baseMonthIdx == null ? '' : monthValue(baseMonthIdx + DO_CONFIG.firstPaymentOffsetMonths);
}

export interface DocDatingDerivation {
  /** Signing and closing fall in different months → the doc month is ambiguous. */
  crossesMonth: boolean;
  /** Funding lands > cutoff days into the next month → 'fund-into' invalid, 'next-month' forced. */
  fundDeepIntoMonth: boolean;
  /** Doc month driving first payment; null = cross-month with no explicit choice yet. */
  docMonthIdx: number | null;
  /** Auto first-payment value ('' when the processor still must pick dating). */
  firstPayment: string;
}

/**
 * Doc-dating rules (Vesting.jsx ClosingSection):
 *  - same month → doc month = closing month, first payment auto-fills;
 *  - cross-month → processor picks: 'fund-into' (signing-month docs) or
 *    'next-month' (re-dated to closing month); no choice ⇒ first payment blank;
 *  - funding day > fundIntoCutoffDays into the next month ⇒ fund-into is
 *    disallowed and 'next-month' is force-selected by the caller.
 */
export function deriveDocDating(
  signingDate: string | null | undefined,
  closingDate: string | null | undefined,
  docDating: 'fund-into' | 'next-month' | null | undefined
): DocDatingDerivation {
  const sIdx = monthIndex(signingDate);
  const cIdx = monthIndex(closingDate);
  const crossesMonth = sIdx != null && cIdx != null && cIdx !== sIdx;
  const fundingDay = closingDate ? Number(closingDate.split('-')[2]) : null;
  const fundDeepIntoMonth =
    crossesMonth && fundingDay != null && Number.isFinite(fundingDay) && fundingDay > DO_CONFIG.fundIntoCutoffDays;
  const docMonthIdx = !crossesMonth
    ? cIdx
    : docDating === 'next-month'
      ? cIdx
      : docDating === 'fund-into'
        ? sIdx
        : null;
  return { crossesMonth, fundDeepIntoMonth, docMonthIdx, firstPayment: firstPayment(docMonthIdx) };
}

export interface ResolvedClosingDates {
  /** Effective dating after the deep-into-month force rule (server-authoritative). */
  docDating: 'fund-into' | 'next-month' | null;
  /** Derived 'YYYY-MM' first payment for the effective dating ('' when unresolved). */
  firstPayment: string;
  derivation: DocDatingDerivation;
}

/**
 * Server-authoritative resolution of the closing-date trio (round-13 finding):
 * applies the deep-into-month force rule (funding > cutoff days into the next
 * month invalidates 'fund-into' → 'next-month'), then derives the first
 * payment for the EFFECTIVE dating. The edge function uses this to overwrite
 * whatever first_payment_date/doc_dating a client sent, and the sign gate
 * re-runs it to verify the stored values match — the browser is never trusted
 * for this invariant. The UI calls the same helper for its optimistic display.
 */
export function resolveClosingDates(
  signingDate: string | null | undefined,
  closingDate: string | null | undefined,
  requestedDating: 'fund-into' | 'next-month' | null | undefined
): ResolvedClosingDates {
  const requested = deriveDocDating(signingDate, closingDate, requestedDating);
  // A dating choice only EXISTS for cross-month files (round-14): same-month
  // (or incomplete) dates normalize any stale/forged choice back to null —
  // otherwise a stored 'fund-into' would lie in wait as a pre-made choice if
  // the dates later cross months. Deep-into-month still forces 'next-month'.
  const docDating: 'fund-into' | 'next-month' | null = requested.fundDeepIntoMonth
    ? 'next-month'
    : requested.crossesMonth
      ? (requestedDating ?? null)
      : null;
  const derivation =
    docDating === (requestedDating ?? null) ? requested : deriveDocDating(signingDate, closingDate, docDating);
  return { docDating, firstPayment: derivation.firstPayment, derivation };
}

const MS_PER_DAY = 86400000;

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function daysBetween(from: string | Date | null | undefined, to: string | Date | null | undefined): number | null {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
}

/** ESS stale: receivedAt age (vs now) ≥ essStaleDays (Documents.jsx:551 shows the chip at ≥). */
export function essIsStale(receivedAt: string | null | undefined, now: Date): boolean {
  const age = daysBetween(receivedAt, now);
  return age != null && age >= DO_CONFIG.essStaleDays;
}

/** Title Commitment stale: age AT CLOSING > titleShelfLifeDays. */
export function titleStaleAtClosing(
  receivedAt: string | null | undefined,
  closingDate: string | null | undefined
): boolean {
  const ageAtClose = daysBetween(receivedAt, closingDate);
  return ageAtClose != null && ageAtClose > DO_CONFIG.titleShelfLifeDays;
}

/**
 * THE display-date parsing contract (R5 — reviews 1.7/2.7/4.8/5.6): exactly
 * FOUR recognized shapes, no engine-dependent Date.parse of loose strings.
 * Date-only and month values keep their LITERAL calendar components (no
 * Date/UTC round-trip — a "07/13/2026" must never drift a day); timestamps
 * are recognized by ISO shape and formatted deterministically in
 * America/Los_Angeles by the shared formatter.
 */
export type ParsedDisplayDate =
  | { kind: 'date'; year: number; month: number; day: number }
  | { kind: 'month'; year: number; month: number }
  | { kind: 'timestamp'; iso: string };

export function parseDisplayDate(value: string | null | undefined): ParsedDisplayDate | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!t) return null;
  // Codex R5-a review finding 6a: validate REAL calendar days — "02/31/2026"
  // must never parse (it could clear Initial CD off a garbage value).
  const daysInMonth = (y: number, m: number) => {
    if (m === 2) {
      const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
      return leap ? 29 : 28;
    }
    return [4, 6, 9, 11].includes(m) ? 30 : 31;
  };
  const okYmd = (y: number, m: number, d: number) =>
    y >= 1900 && y <= 2200 && m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t); // YYYY-MM-DD
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return okYmd(y, mo, d) ? { kind: 'date', year: y, month: mo, day: d } : null;
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t); // MM/DD/YYYY (Encompass US format)
  if (m) {
    const [mo, d, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return okYmd(y, mo, d) ? { kind: 'date', year: y, month: mo, day: d } : null;
  }
  m = /^(\d{4})-(\d{2})$/.exec(t); // YYYY-MM (First Payment wire format)
  if (m) {
    const [y, mo] = [Number(m[1]), Number(m[2])];
    return y >= 1900 && y <= 2200 && mo >= 1 && mo <= 12 ? { kind: 'month', year: y, month: mo } : null;
  }
  // ISO timestamp (mirror rows / event stamps). Postgres text output uses a
  // space separator and a minute-less offset ("+00") — normalize both to
  // strict ES ISO. Codex R5-a review finding 6b: an OFFSET-LESS timestamp is
  // host-dependent under Date.parse (local time), so browser-Pacific and
  // Deno-UTC would render different days — our real producers (Postgres
  // timestamptz, toISOString) always carry an offset, so the rare bare shape
  // is DEFINED as UTC and normalized with an explicit 'Z' before parsing.
  const ts = /^(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}/.exec(t);
  if (ts) {
    // Codex R5-a round-2 finding 2: Date.parse ROLLS impossible calendar days
    // forward (2026-02-31T…Z → Mar 3) — validate the real date part first.
    if (!okYmd(Number(ts[1]), Number(ts[2]), Number(ts[3]))) {
      return null;
    }
    let iso = t.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
    if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso)) {
      iso = `${iso}Z`;
    }
    return Number.isFinite(Date.parse(iso)) ? { kind: 'timestamp', iso } : null;
  }
  return null;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ONE display formatter shared by the UI and the PDF (parity-tested):
 *  date → "Jul 8, 2026" from LITERAL components; month → "Jul 2026";
 *  timestamp → the same style rendered in America/Los_Angeles. '—' when
 *  unparseable. */
export function formatDisplayDate(value: string | null | undefined): string {
  const p = parseDisplayDate(value);
  if (!p) return '—';
  if (p.kind === 'date') return `${MONTHS_SHORT[p.month - 1]} ${p.day}, ${p.year}`;
  if (p.kind === 'month') return `${MONTHS_SHORT[p.month - 1]} ${p.year}`;
  return new Date(p.iso).toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Encompass date normalizer (R4 #1): the API returns placeholder strings for
 * empty date fields — "//", "0/0/", whitespace — which are TRUTHY and were
 * read as real values (a "//" GFE date made every loan look business-purpose,
 * falsely clearing the Initial CD row and masking real CD dates). Applied at
 * the pull boundary AND wherever stored snapshot dates are read, so existing
 * orders self-heal without a re-pull. Valid strings pass through UNCHANGED.
 * R5: validity is the FOUR-format parseDisplayDate contract — never a bare
 * engine-dependent Date.parse (review-5 finding 6).
 */
export function normalizeEncompassDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!t || /^[/0\s.-]*$/.test(t)) return null; // "//", "0/0/0000", "--", ""
  return parseDisplayDate(t) ? t : null;
}

/** Insurance expiry warn (R4 #21 — purchases AND refis, no exemption): the
 *  policy expires under bufferDays after funding/closing. Already-expired
 *  (negative days) warns too — the CALLER distinguishes the copy via
 *  insuranceDaysAfterClose. */
export function insuranceExpiresSoon(
  expiration: string | null | undefined,
  closingDate: string | null | undefined
): boolean {
  const daysAfterClose = daysBetween(closingDate, expiration);
  return daysAfterClose != null && daysAfterClose < DO_CONFIG.insuranceExpirationBufferDays;
}

/** Signed day count from funding→expiration (negative = expired before funding). */
export function insuranceDaysAfterClose(
  expiration: string | null | undefined,
  closingDate: string | null | undefined
): number | null {
  return daysBetween(closingDate, expiration);
}

/** Lock-expired warning: closing date falls after the lock expiration (Snapshot.jsx). */
export function lockExpiredAtClosing(
  lockExpiration: string | null | undefined,
  closingDate: string | null | undefined
): boolean {
  const a = toDate(closingDate);
  const b = toDate(lockExpiration);
  return !!a && !!b && a.getTime() > b.getTime();
}
